import React, { useState, useEffect } from 'react';
import { EditorState } from 'draft-js';
import camelCase from 'lodash/camelCase';

// Utils
import {
  editorStateToJsonString,
  isEmpty,
  createRichTextEditorState,
} from 'Wpds/components/rich-text/utils/EditorState.utils';
import { generateHeaderForQuestion } from 'Shared/utils/Reviews.utils';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';
import { createEmptySelectionState, contentStateWithInsertedText } from 'Shared/utils/draftJsHelpers';

// Components
import { WpContainer, WpContainerHeader, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import { WpButton } from 'Wpds/components/button';
import { WpListSelect } from 'Wpds/components/list';
import { WpRichTextInput } from 'Wpds/components/rich-text';
import { WpCollapsible } from 'Wpds/components/collapsible';
import { WpBadge, WpBadgeList } from 'Wpds/components/badge';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpAlert from 'Wpds/components/alert/WpAlert';
import CustomGPT3Attributes from 'Shared/components/CustomGPT3Attributes/CustomGPT3Attributes';
import { WpText } from 'Wpds/components/text';
import PrivateQuestionPopover from '../ReviewQuestion/PrivateQuestionPopover';

// Apis
import * as api from 'Shared/api/AiSuggestions.api';

// Styles
import './AdjectivesForPropertyQuestion.scss';

type Props = {
  receiverName: string;
  peerReviewFormAnswers?;
  question;
  isOpen: boolean;
  handleMoveNext?: () => void;
  handleMoveBack?: () => void;
  answers;
  handleSetAnswers: Function;
  isSummaryQuestion?: boolean;
  isPreview?: boolean;
};

const AdjectivesForPropertyQuestion = ({
  receiverName,
  peerReviewFormAnswers,
  question,
  isOpen,
  handleMoveNext,
  handleMoveBack,
  answers,
  handleSetAnswers,
  isSummaryQuestion,
  isPreview = false,
}: Props) => {
  const questionProperty = ReviewFormTemplatesHelpers.getQuestionProperty(question);
  const questionKey = ReviewFormTemplatesHelpers.getQuestionKey(question);

  const [selectedAdjectives, setSelectedAdjectives] = useState(answers[questionKey]?.adjectives || []);
  const [editorState, setEditorState] = useState(answers[questionKey]?.explanation || createRichTextEditorState(''));
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [errorMessageForSuggestion, setErrorMessageForSuggestion] = useState(null);

  const negativeAdjectives = ReviewFormTemplatesHelpers.getQuestionNegativeAdjectives(question);
  const positiveAdjectives = ReviewFormTemplatesHelpers.getQuestionPositiveAdjectives(question);
  const [customAdjectives, setCustomAdjectives] = useState(
    selectedAdjectives.filter(adj => ![...positiveAdjectives, ...negativeAdjectives].includes(adj))
  );
  const showBadge = ReviewFormTemplatesHelpers.getQuestionPrivate(question);
  const showOptional = ReviewFormTemplatesHelpers.getQuestionOptional(question);

  const handleSaveAnswers = (adjectives, explanation) => {
    const data = {
      [questionKey]: {
        adjectives: adjectives,
        explanation: editorStateToJsonString(explanation),
      },
    };
    handleSetAnswers(data);
  };

  useEffect(() => {
    if (editorState) handleSaveAnswers(selectedAdjectives, editorState);
  }, [selectedAdjectives, editorState]);

  const handleAdjectivesOnChange = adjectives => {
    setSelectedAdjectives(adjectives.map(adj => adj.value));
  };

  const adjectives = [...positiveAdjectives, ...negativeAdjectives, ...customAdjectives];
  const canMoveNext = isPreview ? true : selectedAdjectives.length > 0 && !isEmpty(editorState);

  const addTextIntoExplanation = text => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const blockKey = selectionState.getFocusKey();
    const blockOffset = selectionState.getFocusOffset();
    const selectionForAnchor = createEmptySelectionState(blockKey, blockOffset, blockOffset);
    const contentStateWithAnchor = contentStateWithInsertedText(
      contentState,
      selectionForAnchor,
      (isEmpty(editorState) ? '' : '\n') + text
    );
    setEditorState(EditorState.push(editorState, contentStateWithAnchor));
  };

  const handleGenerateSummaryClick = () => {
    const summarizedKey = ReviewFormTemplatesHelpers.getSummarizedQuestionKey(question);

    const allPeerReviewFormAnswersForSummarizedQuestion = peerReviewFormAnswers
      .map(answers => answers[summarizedKey] || answers[camelCase(summarizedKey)])
      .filter(answer => answer)
      .map(answer => answer['explanation'])
      .filter(answer => answer);

    setIsLoadingSuggestion(true);
    api
      .createAiSuggestionForReviewSummary({
        recipient_name: receiverName,
        reviews_to_summarize: allPeerReviewFormAnswersForSummarizedQuestion,
      })
      .then(response => {
        addTextIntoExplanation(response.data);
        setErrorMessageForSuggestion(null);
      })
      .catch(error => {
        setErrorMessageForSuggestion(error?.response?.data?.error || 'Unknown error. Please try again.');
      })
      .finally(() => {
        setIsLoadingSuggestion(false);
      });
  };

  const questionHeader = generateHeaderForQuestion({ question });

  const handleGenerateFeedbackClick = () => {
    setIsLoadingSuggestion(true);
    api
      .createAiSuggestionForAdjectives({
        recipient_name: receiverName,
        category: questionProperty,
        question_header: questionHeader,
        adjectives: selectedAdjectives,
      })
      .then(response => {
        addTextIntoExplanation(response.data);
        setErrorMessageForSuggestion(null);
      })
      .catch(error => {
        setErrorMessageForSuggestion(error?.response?.data?.error || 'Unknown error. Please try again.');
      })
      .finally(() => {
        setIsLoadingSuggestion(false);
      });
  };

  const handleAddCustomAdjective = input => {
    setCustomAdjectives([...customAdjectives, input]);
  };

  const questionDescription = ((): string => {
    if (ReviewFormTemplatesHelpers.getQuestionDescription(question)) {
      return ReviewFormTemplatesHelpers.getQuestionDescription(question);
    }

    if (questionProperty) {
      return `What best ${isSummaryQuestion ? 'sums up' : 'describes'} their ${questionProperty.toLowerCase()}?`;
    }
    return null;
  })();

  const baseClassName = 'AdjectivesForPropertyQuestion';
  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <div className={`${baseClassName}__header-container`}>
          <span>
            {questionHeader} {showOptional && <span>(Optional)</span>}
          </span>
          {showBadge && <PrivateQuestionPopover receiverName={receiverName}></PrivateQuestionPopover>}
        </div>
      </WpContainerHeader>
      {!isOpen && selectedAdjectives?.length > 0 && (
        <WpContainerBody>
          <WpBadgeList>
            {selectedAdjectives.map(adjective => (
              <WpBadge size="lg" key={adjective}>
                {adjective}
              </WpBadge>
            ))}
          </WpBadgeList>
        </WpContainerBody>
      )}
      <WpCollapsible open={isOpen}>
        <>
          <WpContainerBody>
            <p className={`${baseClassName}__question`}>
              {questionDescription && <>{questionDescription} </>}
              <WpText tag="span" color="gray-600">
                Select up to 2
              </WpText>
            </p>
            <div className={`${baseClassName}__adjective-list`}>
              <WpListSelect
                id={questionKey}
                onChange={handleAdjectivesOnChange}
                selectedOptions={selectedAdjectives || []}
                options={adjectives}
                direction="horizontal"
                max={2}
                iconSelected={{ name: 'check-circle', prefix: 'fas' }}
              />
              <CustomGPT3Attributes
                handleAddCustomAttribute={handleAddCustomAdjective}
                placeholder=""
                inputId="custom_adjective-input"
                label="Add custom adjective"
                buttonVariant="tertiary"
              />
            </div>
            {!isSummaryQuestion && (
              <div className={`${baseClassName}__explanation`}>
                <div className={`${baseClassName}__explanation__title`}>
                  <WpText bold noMargin>
                    Why did you choose those attributes?
                  </WpText>
                </div>
                <WpText color="gray-600">
                  Want a helpful starting point? Use FeedbackAI to generate a working paragraph, then edit your response
                  to your liking.
                </WpText>
                <div className={`${baseClassName}__button`}>
                  <WpButton variant="secondary" onClick={handleGenerateFeedbackClick} loading={isLoadingSuggestion}>
                    <WpIcon name="magic" />
                    Generate Feedback
                  </WpButton>
                </div>
              </div>
            )}
            {isSummaryQuestion && (
              <div className={`${baseClassName}__explanation`}>
                <div className={`${baseClassName}__explanation__title`}>
                  <WpText bold noMargin>
                    Summarize the reviews on their {questionProperty}. Include your own thoughts.
                  </WpText>
                </div>
                <WpText color="gray-600">
                  Use Feedback AI to jump start thoughtful feedback based on the peer reviews. Then edit it to how you
                  want them to see it.
                </WpText>

                <div className={`${baseClassName}__button`}>
                  <WpButton variant="secondary" onClick={handleGenerateSummaryClick} loading={isLoadingSuggestion}>
                    <WpIcon name="magic" />
                    Generate Summary
                  </WpButton>
                </div>
              </div>
            )}
            {errorMessageForSuggestion && (
              <div className={`${baseClassName}__alert`}>
                <WpAlert type="danger">{errorMessageForSuggestion}</WpAlert>
              </div>
            )}
            <WpRichTextInput
              value={editorState}
              onChange={setEditorState}
              placeholder="We worked together on Project X and..."
              autoFocus={false}
            />
          </WpContainerBody>
          {(handleMoveBack || handleMoveNext) && (
            <WpContainerFooter justifyContent="spaceBetween">
              {handleMoveBack ? (
                <WpButton onClick={handleMoveBack} variant="secondary">
                  <WpIcon name="arrow-left" />
                  Back
                </WpButton>
              ) : (
                <div />
              )}
              {handleMoveNext && (
                <WpButton onClick={handleMoveNext} disabled={!canMoveNext}>
                  Next
                  <WpIcon name="arrow-right" />
                </WpButton>
              )}
            </WpContainerFooter>
          )}
        </>
      </WpCollapsible>
    </WpContainer>
  );
};

export default AdjectivesForPropertyQuestion;
